/**
 * usePageContext — tells the chat agent what page the user is on and what
 * data is visible. Sent with every WS message so the backend can shape
 * responses around the user's current view instead of giving generic replies.
 *
 * Uses window.location directly (not useLocation) so it works safely
 * when ChatProvider mounts outside a Router in tests.
 */

import { useState, useEffect } from 'react'

export interface PageContext {
  route: string
  page: string
  section: string | null
  eventId: number | null
  description: string
}

const FALLBACK: PageContext = { route: '/', page: 'unknown', section: null, eventId: null, description: '' }

const ROUTE_DESCRIPTIONS: Record<string, { page: string; description: string }> = {
  '/home': { page: 'home', description: 'Home dashboard with chat, live event status, and data-anchored invitation cards' },
  '/events': { page: 'events', description: 'Event list showing all operator events with revenue and guest counts' },
  '/analytics': { page: 'analytics', description: 'Analytics hub with links to attractions, plans, kiosks, days, and intelligence' },
  '/analytics/attractions': { page: 'attractions', description: 'Attraction analytics — per-ride scan counts, revenue, repeat play rates' },
  '/analytics/plans': { page: 'plans', description: 'Plan analytics — plan type breakdown, revenue per plan, units sold' },
  '/analytics/kiosks': { page: 'kiosks', description: 'Kiosk analytics — per-kiosk revenue, cash vs card, hourly throughput' },
  '/analytics/users': { page: 'users', description: 'Staff/agent analytics — per-employee scan counts, shift hours, utilization' },
  '/analytics/days': { page: 'days', description: 'Day-by-day analytics — daily revenue, guest counts, hourly patterns' },
  '/analytics/intelligence': { page: 'intelligence', description: 'Event Intelligence — heatmap, revenue velocity, ride capacity & wait times, transit map with carnival layout derived from scan chains, proximity-revenue analysis, staff performance' },
  '/analytics/compare': { page: 'compare', description: 'Side-by-side event comparison — revenue, guests, plans, attractions year-over-year' },
  '/reports': { page: 'reports', description: 'Saved reports and templates — event post-mortem, customer segment, daily summary' },
  '/import': { page: 'smart-import', description: 'Smart Import — upload barcode files for event setup' },
  '/landing': { page: 'landing', description: 'Landing page editor for event e-ticket pages' },
  '/settings': { page: 'settings', description: 'Account settings, preferences, and agent memory' },
  '/help': { page: 'help', description: 'Help center with FAQs, troubleshooting, and field service guides' },
  '/chat/canvas': { page: 'canvas', description: 'Full-screen chat canvas for creating and editing artifacts' },
}

function resolvePageContext(path: string): PageContext {
  // Extract eventId from path segments like /analytics/intelligence/209
  const segments = path.split('/')
  const lastSegment = segments[segments.length - 1]
  const eventId = /^\d+$/.test(lastSegment) ? Number(lastSegment) : null

  // Match exact routes first, then strip trailing ID
  let match = ROUTE_DESCRIPTIONS[path]
  if (!match) {
    const basePath = path.replace(/\/\d+$/, '')
    match = ROUTE_DESCRIPTIONS[basePath]
  }

  if (!match) {
    if (path.includes('/analytics/template/event-post-mortem')) {
      match = { page: 'event-post-mortem', description: 'Event post-mortem report — comprehensive event analysis with revenue, attendance, and operational insights' }
    } else if (path.includes('/analytics/template/customer-segment')) {
      match = { page: 'customer-segment', description: 'Customer segmentation report — guest behavior clusters, plan preferences, engagement tiers' }
    } else if (path.includes('/analytics/template/daily-live')) {
      match = { page: 'daily-live', description: 'Live daily dashboard — real-time revenue, guest flow, hourly trends' }
    } else if (path.includes('/analytics/template/pre-event-readiness')) {
      match = { page: 'pre-event-readiness', description: 'Pre-event readiness checklist — plan configuration, kiosk setup, staffing' }
    } else if (path.startsWith('/events/')) {
      match = { page: 'event-detail', description: 'Event detail page — revenue overview, top attractions, top plans, operational summary' }
    } else if (path.startsWith('/help/')) {
      match = { page: 'help-topic', description: 'Help article with step-by-step instructions' }
    }
  }

  const page = match?.page ?? 'unknown'
  const description = match?.description ?? `Page: ${path}`

  let section: string | null = null
  if (path.includes('/intelligence')) section = 'intelligence'
  else if (path.includes('/attractions')) section = 'attractions'
  else if (path.includes('/plans')) section = 'plans'
  else if (path.includes('/kiosks')) section = 'kiosks'
  else if (path.includes('/compare')) section = 'compare'

  return { route: path, page, section, eventId, description }
}

export function usePageContext(): PageContext {
  const [ctx, setCtx] = useState<PageContext>(() => {
    if (typeof window === 'undefined') return FALLBACK
    return resolvePageContext(window.location.pathname)
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    const update = () => setCtx(resolvePageContext(window.location.pathname))

    // SPA navigation fires popstate; React Router's pushState doesn't, so
    // we also poll on a short interval as a fallback.
    window.addEventListener('popstate', update)
    const timer = setInterval(update, 2000)
    return () => {
      window.removeEventListener('popstate', update)
      clearInterval(timer)
    }
  }, [])

  return ctx
}
